import { useEffect, useRef, useState } from 'react';

import { PlayerNote } from '~/interfaces';
import GameState from '~/modules/game-engine/game-state/game-state';
import { computeComboState, judgedUpToBeat } from '~/modules/game-engine/game-state/helpers/combo';
import InputManager from '~/modules/game-engine/input/input-manager';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { AnnouncedQuality, pickFeedback } from '~/routes/game/singing/game-overlay/components/pick-feedback';
import { ComboSnapshot, trackCombo } from '~/routes/game/singing/game-overlay/helpers/combo-display';
import { ownsSection } from '~/routes/game/singing/game-overlay/helpers/pass-the-mic-turn';

/** How long the pill for one note stays up. */
export const FEEDBACK_VISIBLE_MS = 800;

/** How often finished notes are looked for. Well below a sung note's length. */
const POLL_MS = 100;

export interface PlayerNoteState {
  /** The note just finished, if it was worth remarking on and is still on screen. */
  feedback: { id: number; quality: AnnouncedQuality } | null;
  combo: ComboSnapshot;
}

const EMPTY: PlayerNoteState = { feedback: null, combo: { current: 0, lostAt: null, noteCount: 0 } };

let nextId = 0;

/**
 * How each singer's last note went and how long their run is, one look for both.
 *
 * Polled rather than driven by an event: the only note signal the engine emits (`noteStarted`)
 * fires for player 0 alone and carries nothing, and widening it would mean adding work to the
 * per-frame drawing loop for something the overlay can read for itself. Ten looks a second rather
 * than per frame, because walking every note of every singer sixty times a second is real work and
 * a run only changes when a note finishes.
 */
export default function useNoteFeedback(): Partial<Record<PlayerNumber, PlayerNoteState>> {
  const [states, setStates] = useState<Partial<Record<PlayerNumber, PlayerNoteState>>>({});
  const seenNoteCount = useRef<Partial<Record<PlayerNumber, number>>>({});
  const lastShownAt = useRef<Partial<Record<PlayerNumber, number>>>({});

  useEffect(() => {
    // A set that forgets each timer as it fires, rather than a list that grows for the whole song.
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const poll = setInterval(() => {
      const now = Date.now();

      setStates((current) => {
        let changed = false;
        const next = { ...current };

        const upToBeat = (playerNumber: PlayerNumber) =>
          judgedUpToBeat({
            currentBeat: GameState.getCurrentBeat(),
            inputLagMs: InputManager.getPlayerInputLag(playerNumber),
            beatLengthMs: GameState.getSongBeatLength(),
          });

        for (const player of PlayersManager.getPlayers()) {
          const playerState = GameState.getPlayer(player.number);
          const notes: PlayerNote[] = playerState?.getPlayerNotes() ?? [];
          const tolerance = GameState.getTolerance();
          const previous = current[player.number] ?? EMPTY;

          const combo = trackCombo(current[player.number]?.combo, {
            // Against the track, so the notes this singer let go by break the run as they pass -
            // and only the ones that are theirs to sing, since pass the mic hands the track around.
            current: computeComboState(notes, playerState?.getTrack(), tolerance, {
              upToBeat: upToBeat(player.number),
              ownsSection: (section) => ownsSection(player.number, section),
            }).current,
            noteCount: notes.length,
            now,
          });

          const seen = seenNoteCount.current[player.number];
          seenNoteCount.current[player.number] = notes.length;

          const decision = pickFeedback({
            notes,
            seen,
            lastShownAt: lastShownAt.current[player.number],
            now,
            tolerance,
          });

          let feedback = previous.feedback;
          if (decision) {
            lastShownAt.current[player.number] = now;
            const id = ++nextId;
            feedback = { id, quality: decision.quality };

            const timer = setTimeout(() => {
              timers.delete(timer);
              setStates((latest) => {
                const held = latest[player.number];
                if (held?.feedback?.id !== id) return latest;

                return { ...latest, [player.number]: { ...held, feedback: null } };
              });
            }, FEEDBACK_VISIBLE_MS);
            timers.add(timer);
          }

          if (feedback !== previous.feedback || combo !== previous.combo) {
            changed = true;
            next[player.number] = { feedback, combo };
          }
        }

        return changed ? next : current;
      });
    }, POLL_MS);

    return () => {
      clearInterval(poll);
      timers.forEach(clearTimeout);
    };
  }, []);

  return states;
}
